import { useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  message,
  Space,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ModRule } from "../api";

const TYPE_LABEL: Record<number, string> = {
  0: "关键词",
  1: "正则",
  2: "URL",
};

export default function ModRules() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ["modrules"],
    queryFn: async () =>
      (await api.get<{ items: ModRule[] }>("/modrules")).data.items,
  });

  const upsert = useMutation({
    mutationFn: (v: any) => api.post("/modrules", v),
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["modrules"] });
      setOpen(false);
      form.resetFields();
    },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "保存失败"),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/modrules/${id}`),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["modrules"] });
    },
  });

  return (
    <Card
      style={{ background: "#10131A", border: "1px solid #1A1D26" }}
      title={<span style={{ color: "#F5F5F7" }}>审核规则</span>}
      extra={
        <Button
          type="primary"
          onClick={() => {
            form.resetFields();
            form.setFieldsValue({
              enabled: true,
              rule_type: 0,
              target: "all",
              action: "block",
            });
            setOpen(true);
          }}
        >
          新增规则
        </Button>
      }
    >
      <Table<ModRule>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        pagination={{ pageSize: 30 }}
        columns={[
          {
            title: "Pattern",
            dataIndex: "pattern",
            render: (v: string) => (
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 12 }}>
                {v}
              </span>
            ),
          },
          {
            title: "类型",
            dataIndex: "rule_type",
            width: 100,
            render: (v: number) => <Tag>{TYPE_LABEL[v] ?? v}</Tag>,
          },
          {
            title: "目标",
            dataIndex: "target",
            width: 100,
            render: (v) => <Tag color="purple">{v}</Tag>,
          },
          {
            title: "动作",
            dataIndex: "action",
            width: 100,
            render: (v: string) =>
              v === "block" ? (
                <Tag color="red">阻止</Tag>
              ) : v === "warn" ? (
                <Tag color="orange">警告</Tag>
              ) : (
                <Tag color="blue">送审</Tag>
              ),
          },
          {
            title: "启用",
            dataIndex: "enabled",
            width: 70,
            render: (v: boolean) =>
              v ? <Tag color="green">是</Tag> : <Tag>否</Tag>,
          },
          {
            title: "备注",
            dataIndex: "note",
            ellipsis: true,
            render: (v) => v ?? <span style={{ color: "#6B7280" }}>—</span>,
          },
          {
            title: "操作",
            width: 160,
            render: (_, r) => (
              <Space>
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    form.setFieldsValue(r);
                    setOpen(true);
                  }}
                >
                  编辑
                </Button>
                <Popconfirm
                  title="删除此规则?"
                  onConfirm={() => del.mutate(r.id)}
                >
                  <Button size="small" type="link" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="审核规则"
        open={open}
        onCancel={() => setOpen(false)}
        confirmLoading={upsert.isPending}
        onOk={async () => {
          const v = await form.validateFields();
          upsert.mutate(v);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="pattern"
            label="Pattern"
            rules={[{ required: true }]}
            extra="关键词直接写;正则用 ECMAScript 风格;URL 写域名或全匹配。"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="rule_type"
            label="类型"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 0, label: "关键词" },
                { value: 1, label: "正则" },
                { value: 2, label: "URL" },
              ]}
            />
          </Form.Item>
          <Form.Item name="target" label="目标">
            <Select
              options={[
                { value: "all", label: "all" },
                { value: "msg", label: "msg" },
                { value: "post", label: "post" },
                { value: "user", label: "user" },
                { value: "dapp", label: "dapp" },
              ]}
            />
          </Form.Item>
          <Form.Item name="action" label="动作">
            <Select
              options={[
                { value: "block", label: "阻止" },
                { value: "warn", label: "警告" },
                { value: "review", label: "送审" },
              ]}
            />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
